import { Fragment, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { Alert, Box, Card, CloseButton, Divider, Heading, Icon, Paragraph, Text } from '@zillow/constellation-10';
import { IconChecklistFilled } from '@zillow/constellation-icons';
import type { Answers } from '../../../../models/rental-application';
import { useApplicationListing } from '../../../../contexts/ApplicationContext';
import { getSupplementalSectionTitle, getVisibleSupplementalSections } from './supplementalSections';

type Props = {
  applicationId: string;
  answers: Answers;
  onPersist?: () => Promise<void>;
};

export function SupplementalStep({ applicationId, answers, onPersist }: Props) {
  const { listingOwner } = useApplicationListing();
  const [isIntroDismissed, setIsIntroDismissed] = useState(false);
  const landlordName = listingOwner?.name?.trim() || 'The landlord';
  const sections = getVisibleSupplementalSections(answers);

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Required questions
      </Heading>
      {isEmpty(sections) ? (
        <Paragraph>
          {landlordName} has additional questions for this home. Your answers from earlier steps — like personal,
          household details, income, and residence — help us show you the right questions here. Complete the other steps
          first, then come back to fill this section in.
        </Paragraph>
      ) : (
        <>
          {!isIntroDismissed && (
            <Alert
              tone="info"
              css={{ marginBottom: 'looser' }}
              closeButton={<CloseButton onClick={() => setIsIntroDismissed(true)} />}
            >
              <Text>
                {landlordName} needs a few more details before we can review your application. Answer the questions
                below to keep things moving.
              </Text>
            </Alert>
          )}
          {sections.map((section, index) => (
            <Fragment key={section.id}>
              {index > 0 && <Divider css={{ marginTop: 'looser', marginBottom: 'looser' }} />}
              <Box>
                <Heading level={3} textStyle="body-lg-bold" css={{ marginBottom: 'loose' }}>
                  {getSupplementalSectionTitle(section, index, sections.length)}
                </Heading>
                <section.Component applicationId={applicationId} answers={answers} onPersist={onPersist} />
              </Box>
            </Fragment>
          ))}
        </>
      )}
    </>
  );
}

export function SupplementalStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconChecklistFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
            Why we’re asking
          </Heading>
          <Paragraph>Your future landlord needs these details to finish reviewing your application.</Paragraph>
        </>
      }
    />
  );
}
